import React, {
  BaseSyntheticEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Field, Formik, FormikProps } from "formik";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactSelect from "../../../../components/inputs/select";
import { SelectOption } from "../../../../types";
import { FloatingDatePicker } from "../../../../components/inputs/datePickerInput";
import { StyledCheckbox } from "../../../../components/inputs/styledCheckbox";
import { StyledTimeInput } from "../../../../components/inputs/styledTimeInput";
import { Files, PlusCircle, Trash, Upload, XLg } from "react-bootstrap-icons";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { DAYS, SCHEDULE_FILE_FORMATS, SHORT_DAYS } from "../../../../utils";
import { StyledInput } from "../../../../components/inputs/styledInput";
import { useTimezonesQuery } from "../../../../graphql/country.hooks";
import { ScheduleItem, ScheduleRequest } from "../../types";
import { PageQueryHookResult } from "../../../../graphql/page.hooks";
import { scheduleAccessSchema } from "../../validation/scheduleForm";

type Props = {
  isActive: boolean;
  scheduleRestriction: PageQueryHookResult["data"]["page"]["pageScheduleAssess"];
};

export type ScheduleAccessFormHandle = {
  getFormValues: () => ScheduleRequest;
  submitForm: () => void;
  isFormValid: () => boolean;
};

export const ScheduleAccessForm = forwardRef<ScheduleAccessFormHandle, Props>(
  ({ scheduleRestriction, isActive }, ref) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const formikRef = useRef<FormikProps<ScheduleRequest>>(null);
    const inputFileRef = useRef<HTMLInputElement>(null);

    const [isFilesChanged, setIsFilesChanged] = useState<boolean>(false);

    const { data: timezonesData } = useTimezonesQuery();

    const formatTime = (value: string) => {
      const date = new Date(value);

      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${hours}:${minutes}`;
    };

    const formatedShedules = scheduleRestriction?.pageSchedules?.map(
      (schedule: ScheduleItem) => {
        return {
          ...schedule,
          dateStart: schedule?.dateStart
            ? formatTime(schedule.dateStart)
            : null,
          dateEnd: schedule?.dateEnd ? formatTime(schedule.dateEnd) : null,
        };
      }
    );

    const initialValues: ScheduleRequest = {
      scheduleAccess: scheduleRestriction?.scheduleAccess,
      timezone: scheduleRestriction?.timezone?.id || null,
      dateStart: scheduleRestriction?.dateStart || null,
      dateEnd: scheduleRestriction?.dateEnd || null,
      showNothing: scheduleRestriction?.showNothing ?? true,
      schedules: formatedShedules || [],
      link: scheduleRestriction?.link || "",
      file: scheduleRestriction?.filePath || "",
      fileName: scheduleRestriction?.fileName || "",
      isFilesChanged: false,
    };

    useImperativeHandle(ref, () => ({
      getFormValues: () => {
        const currentValues = formikRef.current?.values || initialValues;
        const onlyActiveSchedules = currentValues?.schedules
          ?.filter((schedule) => schedule?.isActive)
          ?.map(({ __typename, ...rest }) => rest);

        setIsFilesChanged(false);

        return {
          ...currentValues,
          scheduleAccess: isActive,
          isFilesChanged: isFilesChanged,
          schedules: onlyActiveSchedules?.length ? onlyActiveSchedules : null,
        };
      },

      submitForm: () => {
        formikRef.current?.handleSubmit();
      },

      isFormValid: () => {
        return formikRef.current?.isValid ?? false;
      },
    }));

    const timezoneOptions =
      timezonesData?.timezones?.map((timezone) => {
        return {
          label: `(GMT ${timezone?.gMT}) ${timezone?.name}`,
          value: timezone?.id,
        };
      }) || [];

    const openFileManagement = () => {
      if (!inputFileRef?.current) return;

      inputFileRef?.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result as string;

        formikRef.current?.setFieldValue("file", base64);
        formikRef.current?.setFieldValue("fileName", file.name);
        setIsFilesChanged(true);
      };

      reader.readAsDataURL(file);
    };

    const clearFile = () => {
      formikRef.current?.setFieldValue("file", null);
      formikRef.current?.setFieldValue("fileName", null);
      setIsFilesChanged(true);
    };

    if (!isActive) {
      return <></>;
    }

    return (
      <div className="mb-4">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={scheduleAccessSchema(t)}
          onSubmit={() => {}}
          enableReinitialize
        >
          {({ touched, errors, setFieldValue, values }) => {
            return (
              <Form>
                <Field name="timezone">
                  {() => (
                    <ReactSelect
                      className="mb-3 text-dark-gray"
                      options={timezoneOptions}
                      label={t("page.Timezone")}
                      value={timezoneOptions.find(
                        (timezone) => timezone.value === values.timezone
                      )}
                      onChange={(option: SelectOption) => {
                        setFieldValue("timezone", option.value);
                      }}
                    />
                  )}
                </Field>
                <div className="d-flex align-items-center mb-3 mb-md-4">
                  <FloatingDatePicker
                    label={t("page.Date Start")}
                    value={values.dateStart}
                    onChange={(date) =>
                      setFieldValue("dateStart", date.toDateString())
                    }
                    placeholder="mm.dd.yyyy"
                    className="flex-grow-1"
                    maxDate={values.dateEnd || null}
                  />
                  <div
                    className="d-none d-md-block bg-dark mx-3"
                    style={{ width: "15px", height: "1.25px" }}
                  ></div>
                  <FloatingDatePicker
                    label={t("page.Date End")}
                    value={values.dateEnd}
                    onChange={(date) =>
                      setFieldValue("dateEnd", date.toDateString())
                    }
                    placeholder="mm.dd.yyyy"
                    className="flex-grow-1 ms-3 ms-md-0"
                    minDate={values.dateStart || null}
                  />
                </div>
                <p className="text-dark fw-bold mb-3">{t("page.Schedule")}</p>
                <div className="d-grid gap-4 gap-md-3 mb-4">
                  {DAYS.map((day) => {
                    const daySchedules = Array.isArray(values.schedules)
                      ? values.schedules.filter((s) => s.day === day)
                      : [];
                    const isDisabled = !daySchedules[0]?.isActive;

                    if (daySchedules.length === 0) {
                      const newSchedule: ScheduleItem = {
                        day,
                        isActive: false,
                        dateStart: null,
                        dateEnd: null,
                      };
                      setFieldValue("schedules", [
                        ...(values.schedules || []),
                        newSchedule,
                      ]);
                      return null;
                    }

                    return (
                      <div
                        key={day}
                        className="d-flex flex-wrap align-items-start"
                      >
                        <StyledCheckbox
                          controlId={`day-${day}`}
                          label={t(`page.${isMobile ? SHORT_DAYS[day] : day}`)}
                          className="day-checkbox col-2 text-break me-3 mt-2 me-md-2 text-nowrap"
                          checked={daySchedules.every((s) => s.isActive)}
                          onChange={() => {
                            const newSchedules = values.schedules.map(
                              (s: ScheduleItem) =>
                                s.day === day
                                  ? { ...s, isActive: !s.isActive }
                                  : s
                            );
                            setFieldValue("schedules", newSchedules);
                          }}
                        />

                        <div className="d-flex flex-column gap-4 gap-md-3">
                          {daySchedules.map((period: ScheduleItem) => {
                            const globalIndex =
                              values.schedules.indexOf(period);

                            return (
                              <div
                                key={globalIndex}
                                className="d-flex align-items-center"
                              >
                                <StyledTimeInput
                                  name={`schedules.${globalIndex}.dateStart`}
                                  className={`flex-grow-1 ${isDisabled && "pe-none opacity-25"}`}
                                  value={period.dateStart || ""}
                                  max={period.dateEnd || ""}
                                  onChange={(e: BaseSyntheticEvent) => {
                                    const newSchedules = [...values.schedules];
                                    newSchedules[globalIndex].dateStart =
                                      e.target.value;
                                    setFieldValue("schedules", newSchedules);
                                  }}
                                />

                                <div
                                  className="bg-dark mx-2"
                                  style={{ width: "7px", height: "0.8px" }}
                                />

                                <StyledTimeInput
                                  name={`schedules.${globalIndex}.dateEnd`}
                                  className={`flex-grow-1 ${isDisabled && "pe-none opacity-25"}`}
                                  value={period.dateEnd || ""}
                                  min={period.dateStart || ""}
                                  onChange={(e: BaseSyntheticEvent) => {
                                    const newSchedules = [...values.schedules];
                                    newSchedules[globalIndex].dateEnd =
                                      e.target.value;
                                    setFieldValue("schedules", newSchedules);
                                  }}
                                />

                                <Trash
                                  className={`text-dark ms-2 me-3 cursor-pointer ${isDisabled && "pe-none opacity-25"}`}
                                  style={{ fontSize: "12px" }}
                                  onClick={() => {
                                    const newSchedules = [...values.schedules];

                                    if (daySchedules.length === 1) {
                                      newSchedules[globalIndex] = {
                                        ...newSchedules[globalIndex],
                                        dateStart: null,
                                        dateEnd: null,
                                      };
                                    } else {
                                      newSchedules.splice(globalIndex, 1);
                                    }

                                    setFieldValue("schedules", newSchedules);
                                  }}
                                />
                                <PlusCircle
                                  className={`text-dark cursor-pointer ${isDisabled && "pe-none opacity-25"}`}
                                  style={{ fontSize: "12px" }}
                                  onClick={() => {
                                    if (daySchedules.length >= 3) return;
                                    const newSchedules = [
                                      ...values.schedules,
                                      {
                                        day,
                                        isActive: true,
                                        dateStart: "",
                                        dateEnd: "",
                                      },
                                    ];
                                    setFieldValue("schedules", newSchedules);
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        {day === DAYS[0] && (
                          <Button
                            variant={`${isMobile ? "outline-primary" : "transparent"}`}
                            className="col-12 col-md-auto d-flex align-items-center justify-content-center ms-md-3 mt-3 mt-md-1 py-2 p-md-0 rounded-2 no-hover"
                            onClick={() => {
                              const firstFilled = values.schedules.find(
                                (s: ScheduleItem) => s.dateStart && s.dateEnd
                              );

                              const templateStart = firstFilled
                                ? firstFilled.dateStart
                                : null;
                              const templateEnd = firstFilled
                                ? firstFilled.dateEnd
                                : null;

                              const updatedSchedule = values.schedules.map(
                                (day: ScheduleItem) => ({
                                  ...day,
                                  isActive: true,
                                  dateStart: templateStart,
                                  dateEnd: templateEnd,
                                })
                              );

                              setFieldValue("schedules", updatedSchedule);
                            }}
                          >
                            <Files
                              className="text-primary me-2 cursor-pointer"
                              style={{ fontSize: "12px" }}
                            />
                            <p className="text-primary fs-7 fw-normal m-0 cursor-pointer">
                              {t("page.Apply to All Days")}
                            </p>
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3">
                  <p className="col-11 col-md text-dark fs-6 fw-bold mb-2 mb-md-0">
                    {t("page.outsideContentText")}
                  </p>
                  <StyledCheckbox
                    controlId="showNothing"
                    label={t("page.Show Nothing")}
                    className="show-content-checkbox"
                    checked={values.showNothing}
                    onChange={() =>
                      setFieldValue("showNothing", !values.showNothing)
                    }
                  />
                </div>
                <div className="form-floating mb-3">
                  <StyledInput
                    disabled={values.showNothing}
                    name="link"
                    className="text-dark-gray"
                    label={t("page.Additional Link")}
                    error={!!(touched.link && errors.link)}
                  />
                </div>
                <div className="form-floating">
                  <input
                    ref={inputFileRef}
                    className="d-none"
                    type="file"
                    accept={SCHEDULE_FILE_FORMATS}
                    onChange={handleFileChange}
                  />
                  {!values.file ? (
                    <Button
                      variant="outline-primary"
                      className="col-12 py-3 d-flex align-items-center justify-content-center primary-hover rounded"
                      disabled={values.showNothing}
                      onClick={openFileManagement}
                    >
                      <Upload className="text-primary me-2" />
                      <p className="text-primary m-0">
                        {t("page.Upload File")}
                      </p>
                    </Button>
                  ) : (
                    <div className="col-12 d-flex align-items-center justify-content-between bg-sky rounded py-2 px-3">
                      <div className="col-10 d-flex align-items-center">
                        <img
                          className="me-3"
                          src={values.file}
                          alt={values.fileName}
                          height={40}
                        />
                        <p className="text-dark-gray m-0 overflow-ellipsis text-nowrap overflow-hidden col-5">
                          {values.fileName}
                        </p>
                      </div>

                      <XLg
                        className="text-dark-gray cursor-pointer fw-bold"
                        onClick={clearFile}
                      />
                    </div>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }
);

ScheduleAccessForm.displayName = "ScheduleAccessForm";
